
import { TableauCourse } from './types';

export const TABLEAU_INTERMEDIATE_COURSE: TableauCourse = {
  id: 'tableau-intermediate',
  title: 'Tableau Intermediate: Advanced Analytics & Dashboard Design',
  description: 'Advance your Tableau skills with complex calculations, advanced chart types, and professional dashboard design principles.',
  level: 'intermediate',
  totalDuration: '15-18 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed Tableau Fundamentals course',
    'Experience with basic Tableau visualizations',
    'Understanding of data analysis concepts',
    'Familiarity with SQL basics'
  ],
  learningOutcomes: [
    'Create advanced calculated fields and table calculations',
    'Design professional, interactive dashboards',
    'Implement advanced chart types and custom visualizations',
    'Master parameters and dynamic filtering',
    'Optimize dashboard performance and user experience',
    'Apply advanced analytics and statistical functions'
  ],
  modules: [
    {
      id: 'advanced-calculations',
      title: 'Advanced Calculations and Table Calculations',
      description: 'Master complex calculated fields, table calculations, and level of detail expressions.',
      content: `
# Advanced Calculations and Table Calculations

## Overview

Advanced calculations in Tableau enable sophisticated data analysis and custom metrics. This module covers calculated fields, table calculations, and Level of Detail (LOD) expressions.

## Calculated Fields

### Basic Calculated Fields
Calculated fields allow you to create new data from existing data using functions and operators.

### Syntax and Functions
- **String Functions**: LEFT(), RIGHT(), CONTAINS(), SPLIT()
- **Date Functions**: DATEADD(), DATEDIFF(), DATEPART()
- **Numeric Functions**: ROUND(), ABS(), SQRT(), POWER()
- **Logical Functions**: IF(), CASE(), ISNULL(), ZN()

### Advanced Examples

#### Sales Performance Categories
\`\`\`
IF [Sales] > 100000 THEN "High Performer"
ELSEIF [Sales] > 50000 THEN "Medium Performer"
ELSE "Needs Improvement"
END
\`\`\`

#### Year-over-Year Growth
\`\`\`
(SUM([Sales]) - LOOKUP(SUM([Sales]), -1)) / LOOKUP(SUM([Sales]), -1)
\`\`\`

## Table Calculations

Table calculations perform computations on values in the visualization, not the underlying data source.

### Common Table Calculations
- **Running Total**: Cumulative sum of values
- **Percent of Total**: Proportion of each value to the total
- **Rank**: Ordering of values
- **Moving Average**: Smoothed trend analysis

### Quick Table Calculations
Tableau provides built-in table calculations accessible via right-click:
1. Right-click on a measure
2. Select "Quick Table Calculation"
3. Choose from predefined options

## Level of Detail (LOD) Expressions

LOD expressions allow you to compute values at different levels of granularity.

### Types of LOD Expressions

#### FIXED
Computes values at a specified level of detail regardless of view dimensions.
\`\`\`
{FIXED [Customer ID] : SUM([Sales])}
\`\`\`

#### INCLUDE
Adds dimensions to the view level of detail.
\`\`\`
{INCLUDE [Category] : AVG([Sales])}
\`\`\`

#### EXCLUDE
Removes dimensions from the view level of detail.
\`\`\`
{EXCLUDE [Quarter] : SUM([Sales])}
\`\`\`

## Practical Applications

### Customer Cohort Analysis
Analyze customer behavior over time using cohort-based calculations.

### Market Basket Analysis
Identify product associations and cross-selling opportunities.

### Statistical Analysis
Implement standard deviation, correlation, and regression analysis.

## Best Practices

1. **Performance**: Use table calculations sparingly for large datasets
2. **Clarity**: Document complex calculations with comments
3. **Reusability**: Create calculated fields that can be reused across worksheets
4. **Testing**: Validate calculations with known results

## Hands-On Exercise

Create a comprehensive sales analysis using:
- Customer lifetime value calculations
- Year-over-year growth analysis
- Performance ranking system
- Moving averages for trend analysis
      `,
      duration: '75 minutes',
      difficulty: 'intermediate',
      order: 1,
      estimatedTime: '75 minutes',
      objectives: [
        'Create complex calculated fields using various function types',
        'Implement table calculations for trend analysis',
        'Master Level of Detail (LOD) expressions',
        'Apply advanced calculations to real-world business scenarios'
      ],
      quiz: {
        questions: [
          {
            id: 'tableau-calc-q1',
            question: 'Which LOD expression type computes values regardless of view dimensions?',
            options: [
              'INCLUDE',
              'EXCLUDE',
              'FIXED',
              'DETAIL'
            ],
            correctAnswer: 2,
            difficulty: 'medium',
            explanation: 'FIXED LOD expressions compute values at a specified level of detail regardless of the dimensions in the view, making them useful for creating consistent reference values.',
            points: 2,
            type: 'multiple-choice'
          },
          {
            id: 'tableau-calc-q2',
            question: 'Table calculations are computed on which data?',
            options: [
              'The underlying data source',
              'Values in the current visualization',
              'A separate calculation table',
              'The entire database'
            ],
            correctAnswer: 1,
            difficulty: 'medium',
            explanation: 'Table calculations perform computations on the values that are currently in the visualization, not on the underlying data source, which makes them dependent on the view structure.',
            points: 2,
            type: 'multiple-choice'
          },
          {
            id: 'tableau-calc-q3',
            question: 'Which function would you use to calculate the number of days between two dates?',
            options: [
              'DATEADD()',
              'DATEDIFF()',
              'DATEPART()',
              'DATENAME()'
            ],
            correctAnswer: 1,
            difficulty: 'easy',
            explanation: 'DATEDIFF() calculates the difference between two dates in specified units (days, months, years), while DATEADD() adds time to a date and DATEPART() extracts parts of dates.',
            points: 1,
            type: 'multiple-choice'
          }
        ],
        passThreshold: 75
      }
    }
  ]
};
